/**
 * Runtime Configuration System
 * 
 * This module uses runtime config injection to support single-build deployments.
 * Configuration is loaded from window['acast/config'] (injected by /config.js)
 * with fallbacks to build-time environment variables.
 * 
 * ⚠️ TESTING: Tests must initialize window['acast/config'] before importing this module.
 * The test/setup.tsx automatically imports test/mockConfig.ts to handle this.
 * 
 * @see ../../docs/adr/0002-single-dashboard-builds.md - Architecture decision and trade-offs
 * @see docs/CONFIG.md - Config system guide
 * @see test/mockConfig.ts - Test utilities
 */

import { getConfig } from '@acast-tech/product-suite-app-shell';

interface AppConfig {
  env: 'development' | 'production';
  revenueApiUrl: string;
  sentry: string;
  auth0ClientId: string;
  launchDarklyClientId: string;
}

interface WindowWithConfig extends Window {
  'acast/config'?: AppConfig;
}

export const isDevelopment = process.env.NODE_ENV === 'development';

// Use a getter to ensure config is evaluated after runtime config is loaded
function getAppConfig(): Partial<AppConfig> {
  const config = (window as WindowWithConfig)['acast/config'];
  
  // In test/CI environments, log a warning if config isn't initialized
  if (!config && typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    console.warn(
      '[Config] window["acast/config"] not initialized. ' +
      'Tests should import test/mockConfig.ts before using config.'
    );
  }
  
  return config ?? {};
}

// Helper to get environment from runtime config
function getEnvFromConfig(): 'development' | 'production' {
  const appConfig = getAppConfig();
  
  if (appConfig.env) {
    return appConfig.env;
  }
  
  // Fallback: Only use for local development (vite dev server)
  // In production builds, this should never be reached as /config.js should load first
  console.warn(
    '[Config] window["acast/config"].env not found. ' +
    'This suggests /config.js failed to load. Falling back to build-time env. ' +
    'Check network tab for /config.js request.'
  );
  
  // Use build-time VITE env var for fallback (only available in dev mode)
  return import.meta.env.DEV ? 'development' : 'production';
}

// Derive isProduction from runtime config when available - use getter for lazy evaluation
export const isProduction = getEnvFromConfig() === 'production';

// Build config object, evaluated lazily to ensure window['acast/config'] is loaded
const buildConfig = () => {
  const appConfig = getAppConfig();
  
  // Prefer runtime config from window['acast/config'], fallback to build-time env vars
  // Use runtime config env if available to support single build artifacts across environments
  const env = getEnvFromConfig();
  const isProduction = env === 'production';
  
  const auth0ClientId = appConfig.auth0ClientId || import.meta.env.VITE_AUTH0_CLIENT_ID;
  const launchDarklyClientId =
    appConfig.launchDarklyClientId || import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;
  const revenueApiUrl = appConfig.revenueApiUrl || import.meta.env.VITE_REVENUE_API_URL || '';
  
  const cmsUrl = isProduction ? 'https://open.acast.com' : 'https://open.dev.satellite.acast.cloud';
  const graphqlApiUrl = isProduction
    ? 'https://graphql-api.prod.vision.acast.com'
    : 'https://graphql-api.dev.vision.acast.cloud';
  const hotjarConfig = {
    siteId: 6380511,
    hotjarVersion: 6,
  };
  const sentryConfig = {
    dsn: 'https://29a1d28e341f56c34b895d9d56535fbd@o66526.ingest.us.sentry.io/4509229734363136',
    sendDefaultPii: true,
  };
  const mixpanelConfig = {
    projectToken: isProduction
      ? '8720afe86b0733c87cc8afc40d2467f4'
      : '8ee276f6848c8ddafbf8c30cf16f77a6',
  };
  
  return {
    ...getConfig(env),
    ...appConfig,
    env,
    revenueApiUrl,
    cmsUrl,
    graphqlApiUrl,
    auth0ClientId,
    launchDarklyClientId,
    hotjarConfig,
    sentryConfig,
    mixpanelConfig,
  };
};

// Export config - since /config.js loads before /src/main.tsx in index.html,
// window['acast/config'] should be available by the time this module evaluates
export const config = buildConfig();

// Debug logging to help diagnose config issues
if (typeof window !== 'undefined') {
  console.log('[Config] Dashboard config initialized:', {
    env: config.env,
    cmsUrl: config.cmsUrl,
    hasRuntimeConfig: !!(window as WindowWithConfig)['acast/config'],
    runtimeConfigEnv: (window as WindowWithConfig)['acast/config']?.env,
    importMetaDev: import.meta.env.DEV,
    importMetaMode: import.meta.env.MODE,
  });
}
